import Phaser from "phaser";
import { Direction } from "../core/model";

export class VirtualJoystick {
  private joystickBase?: Phaser.GameObjects.Arc;
  private joystickThumb?: Phaser.GameObjects.Arc;
  private joystickPointerId: number | null = null;
  private joystickLastDir: Direction | null = null;
  public isMobileLayout = false;



  // Keep bound handlers so we can remove them in destroy()
  private handlePointerDown = (pointer: Phaser.Input.Pointer) => {
    if (!this.joystickBase || !this.joystickThumb) return;

    const cx = this.joystickBase.x;
    const cy = this.joystickBase.y;
    const dist = Math.hypot(pointer.x - cx, pointer.y - cy);
    const fireRadius = this.joystickThumb.radius * 1.2;

    // Tap near the red dot = fire laser
    if (dist <= fireRadius && this.canFire()) {
      this.onFire();
    }

    // Start tracking this pointer for movement
    if (this.joystickPointerId !== null) return;
    this.joystickPointerId = pointer.id;
    this.joystickLastDir = null;

    // Thumb starts at center; base stays fixed
    this.joystickThumb.setPosition(cx, cy);
  };

  private handlePointerMove = (pointer: Phaser.Input.Pointer) => {
    if (pointer.id !== this.joystickPointerId) return;
    if (!this.joystickBase || !this.joystickThumb) return;

    const cx = this.joystickBase.x;
    const cy = this.joystickBase.y;
    let dx = pointer.x - cx;
    let dy = pointer.y - cy;

    const dist = Math.hypot(dx, dy);
    const maxOffset = this.joystickBase.radius * 0.75;

    if (dist > maxOffset && dist > 0) {
      const scale = maxOffset / dist;
      dx *= scale;
      dy *= scale;
    }

    this.joystickThumb.setPosition(cx + dx, cy + dy);

    // Small dead zone
    const deadZone = this.joystickBase.radius * 0.1;
    if (dist < deadZone) return;

    let dir: Direction;
    if (Math.abs(dx) > Math.abs(dy)) {
      dir = dx > 0 ? { dx: 1, dy: 0 } : { dx: -1, dy: 0 };
    } else {
      dir = dy > 0 ? { dx: 0, dy: 1 } : { dx: 0, dy: -1 };
    }

    if (
      !this.joystickLastDir ||
      dir.dx !== this.joystickLastDir.dx ||
      dir.dy !== this.joystickLastDir.dy
    ) {
      this.joystickLastDir = dir;
      if (this.canHandleInput()) {
        this.onDirection(dir);
      }
    }
  };

  private handlePointerUp = (pointer: Phaser.Input.Pointer) => {
    if (pointer.id !== this.joystickPointerId) return;
    this.joystickPointerId = null;
    this.joystickLastDir = null;
    if (this.joystickBase && this.joystickThumb) {
      this.joystickThumb.setPosition(
        this.joystickBase.x,
        this.joystickBase.y
      );
    }
  };

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly canHandleInput: () => boolean,
    private readonly canFire: () => boolean,
    private readonly onDirection: (dir: Direction) => void,
    private readonly onFire: () => void
  ) {}

  create(): void {
    const width = this.scene.scale.width;
    const height = this.scene.scale.height;

    // Aspect-ratio heuristic ONLY
    const isMobileLayout = height > width || width < 640;
    this.isMobileLayout = isMobileLayout;
    if (!isMobileLayout) return;

    // Reserve bottom slice for controls (and debug box)
    const controlsTop = height * 0.6;
    const controlsHeight = height - controlsTop;



    const radius = Math.max(70, Math.min(width, height) * 0.2);

    // Centered horizontally in that bottom slice
    const baseX = width / 2;
    const baseY = height - radius - 40;

    this.joystickBase = this.scene.add.circle(
      baseX,
      baseY,
      radius,
      0xffffff,
      0.08
    );
    this.joystickBase
      .setStrokeStyle(2, 0xffffff)
      .setDepth(500)
      .setScrollFactor(0)
      .setVisible(true);

    this.joystickThumb = this.scene.add.circle(
      baseX,
      baseY,
      radius * 0.4,
      0xff0000,
      0.9
    );
    this.joystickThumb
      .setDepth(501)
      .setScrollFactor(0)
      .setVisible(true);

    // Scene-level pointer events
    this.scene.input.on("pointerdown", this.handlePointerDown, this);
    this.scene.input.on("pointermove", this.handlePointerMove, this);
    this.scene.input.on("pointerup", this.handlePointerUp, this);
    this.scene.input.on("pointerupoutside", this.handlePointerUp, this);
  }

  destroy(): void {
    if (!this.isMobileLayout) return;

    this.scene.input.off("pointerdown", this.handlePointerDown, this);
    this.scene.input.off("pointermove", this.handlePointerMove, this);
    this.scene.input.off("pointerup", this.handlePointerUp, this);
    this.scene.input.off("pointerupoutside", this.handlePointerUp, this);

    this.joystickBase?.destroy();
    this.joystickThumb?.destroy();


    this.joystickBase = undefined;
    this.joystickThumb = undefined;

    this.joystickPointerId = null;
    this.joystickLastDir = null;
  }
}
